import { NO_REPLACE_INDEX } from '@apeiron/apostle'
import { ListingStatus } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import {
  PlanetLite,
  PlanetMiddleLite,
  PlanetStage,
  PlanetStatus
} from '@apeiron/planet'
import { styled } from '@mui/material'
import ApostleAttachReforgeLayout from '@src/components/apostles/manage/ApostleAttachReforgeLayout'
import ApostleAttachRow from '@src/components/apostles/manage/attach/ApostleAttachRow'
import { FetchParamsCallback } from '@src/components/planet/list/PlanetSelectPopup'
import ApeironDialog from '@src/components/share/apeiron/ApeironDialog'
import { useApostleAttachContext } from '@src/contexts/apostle/ApostleAttachContext'
import { GetMyPlanetsRequest } from '@src/hooks/graphql/useGetMyPlanets'
import {
  PlanetLiteDeserializeMode,
  PlanetLiteDeserializeOption
} from '@src/types/deserialize'
import * as R from 'ramda'
import { FC, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

const SavePopup = styled(ApeironDialog)`
  .Custom-Text {
    strong {
      color: red !important;
      font-weight: 400;
    }
  }
`

const ApostleAttachView: FC<Props> = (props: Props) => {
  const { className } = props

  const { config } = useFrontendConfigContext()

  const { t } = useTranslation()

  const {
    displayAttachTable,
    selectedPlanets,
    checkAnyUpdate,
    saveAttachment,
    removePlanet,
    resetApostle,
    replacePlanet,
    updatePlanets
  } = useApostleAttachContext()

  const [openPlanetPopup, setOpenPlanetPopup] = useState(false)

  const [openSavePopup, setOpenSavePopup] = useState(false)

  const [replaceIndex, setReplaceIndex] = useState<number>(NO_REPLACE_INDEX)

  const [selectLimit, setSelectLimit] = useState<number>(0)

  const hasUpdate = useMemo(() => checkAnyUpdate(), [checkAnyUpdate])

  const handleOnResetSelection = useCallback(() => {
    resetApostle()
  }, [resetApostle])

  const handleOnSavePopupOpen = useCallback(() => {
    setOpenSavePopup(true)
  }, [])

  const handleOnSavePopupClose = useCallback(() => {
    setOpenSavePopup(false)
  }, [])

  const handleOnSave = useCallback(async () => {
    setOpenSavePopup(false)
    await saveAttachment()
  }, [saveAttachment])

  const handleOnCheckHaveChanges = useCallback(() => {
    return hasUpdate
  }, [hasUpdate])

  const apostleLimit = useMemo(() => {
    return config?.managePlanetApostle.maxManageCount || 0
  }, [config?.managePlanetApostle.maxManageCount])

  const planetLimit = useMemo(() => {
    return config?.managePlanetApostle.maxSelectedPlanet || 0
  }, [config?.managePlanetApostle.maxSelectedPlanet])

  const handleOnPlanetAdd = useCallback(() => {
    const logicLimit = config?.managePlanetApostle.maxSelectedPlanet || 0

    setSelectLimit(
      Math.max(logicLimit - R.length(R.keys(displayAttachTable)), 0)
    )
    setReplaceIndex(NO_REPLACE_INDEX)
    setOpenPlanetPopup(true)
  }, [config?.managePlanetApostle.maxSelectedPlanet, displayAttachTable])

  const handleOnPlanetChange = useCallback((index: number) => {
    setSelectLimit(1)
    setReplaceIndex(index)
    setOpenPlanetPopup(true)
  }, [])

  const handleOnPlanetRemove = useCallback(
    (planetId: string) => {
      removePlanet(planetId)
    },
    [removePlanet]
  )

  const handleOnPlanetClose = useCallback(() => {
    setReplaceIndex(NO_REPLACE_INDEX)
    setOpenPlanetPopup(false)
  }, [])

  const handleOnPlanetSelect = useCallback(
    (planets: PlanetLite[]) => {
      if (replaceIndex === NO_REPLACE_INDEX) {
        updatePlanets(planets)
      } else {
        const planet = R.head(planets)

        if (R.isNotNil(planet)) {
          replacePlanet(planet, replaceIndex)
        }
      }

      setReplaceIndex(NO_REPLACE_INDEX)
      setOpenPlanetPopup(false)
    },
    [replaceIndex, replacePlanet, updatePlanets]
  )

  const handleOnPlanetPostProcess = useCallback(
    (planet: PlanetLite) => {
      const isSelected = R.find(
        R.propEq(planet.planetID, 'planetID'),
        selectedPlanets
      )
        ? 'common.selected'
        : undefined

      const isOrbiting = R.propEq(PlanetStatus.StakedToStar, 'status', planet)
        ? 'planet.card.status.orbiting'
        : undefined

      const translateKey = isSelected || isOrbiting

      return translateKey
        ? {
            ...planet,
            disabled: true,
            bottomStatus: {
              translationKey: translateKey
            }
          }
        : planet
    },
    [selectedPlanets]
  )

  const handleOnPlanetDisable = useCallback((planet: PlanetLite) => {
    if (
      R.isNotNil(planet?.bottomStatus?.translationKey) &&
      !R.isEmpty(planet?.bottomStatus?.translationKey) &&
      planet?.bottomStatus?.translationKey !== 'common.selected'
    ) {
      return planet?.bottomStatus?.translationKey
    } else return null
  }, [])

  const handleOnPlanetFetchParams: FetchParamsCallback =
    useCallback(async () => {
      const getMyPlanetsRequest: GetMyPlanetsRequest = {
        stage: [PlanetStage.Planet],
        nftListingStatus: ListingStatus.UNLISTING
      }

      const getMyPlanetsOption: PlanetLiteDeserializeOption = {
        mode: PlanetLiteDeserializeMode.ATTACH_BY_APOSTLE
      }

      return [getMyPlanetsRequest, getMyPlanetsOption]
    }, [])

  const handleOnRenderRow = useCallback(
    (planet: PlanetMiddleLite, key: string, index: number) => {
      return (
        <ApostleAttachRow
          key={key}
          planet={planet}
          onPlanetClick={() => {
            handleOnPlanetChange(index)
          }}
          onPlanetRemove={handleOnPlanetRemove}
        />
      )
    },
    [handleOnPlanetChange, handleOnPlanetRemove]
  )

  const handleOnRenderEmptyRow = useCallback(() => {
    return <ApostleAttachRow onPlanetClick={handleOnPlanetAdd} />
  }, [handleOnPlanetAdd])

  return (
    <>
      <ApostleAttachReforgeLayout
        className={className}
        hasUpdate={hasUpdate}
        planetLimit={planetLimit}
        selectedPlanets={selectedPlanets}
        selectLimit={selectLimit}
        subtitle={
          <Trans>
            {t('sanctuary.manage_apostle.subtitle', {
              count: apostleLimit
            })}
          </Trans>
        }
        title={t('sanctuary.manage_apostle.title_attach')}
        confirmButton={{
          text: t('sanctuary.manage_apostle.tab.attach'),
          onClick: handleOnSavePopupOpen
        }}
        resetButton={{
          text: t('common.button.reset'),
          onClick: handleOnResetSelection
        }}
        planetPopup={{
          open: openPlanetPopup,
          onFetchParams: handleOnPlanetFetchParams,
          onDisable: handleOnPlanetDisable,
          onClose: handleOnPlanetClose,
          onSelect: handleOnPlanetSelect,
          onPostProcess: handleOnPlanetPostProcess
        }}
        onCheckHaveChanges={handleOnCheckHaveChanges}
        onRenderRow={handleOnRenderRow}
        onRenderEmptyRow={handleOnRenderEmptyRow}
      />
      <SavePopup
        open={openSavePopup}
        leftButton={t('common.cancel')}
        rightButton={t('common.button.yes')}
        onClose={handleOnSavePopupClose}
        onLeftClick={handleOnSavePopupClose}
        onRightClick={handleOnSave}
      >
        {t('sanctuary.manage_apostle.save_popup')}
      </SavePopup>
    </>
  )
}

type Props = {
  className?: string
}

export default ApostleAttachView
